import React from "react";
import { useMemo } from "react";
import { GridDataTableColumn } from "../GridDataTable/GridDataTableColumn";

/**
 * Options accepted by `useGridTextEllipsisCell`.
 */
export interface UseGridTextEllipsisCellOptions {
  /** The column definitions that may contain the `ellipsisCell` flag. */
  columns: GridDataTableColumn[];
}

/** Stable style object shared by all ellipsis cells — hoisted to avoid
 *  creating a new object on every render. */
const ellipsisStyle: React.CSSProperties = {
  width: "100%",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "block",
};

/**
 * Single, module-scoped Cell renderer that truncates long text with an
 * ellipsis and shows the full value on hover via the `title` attribute.
 *
 * Because neither the component nor the style object close over any
 * per-column state, one shared reference is sufficient — this avoids
 * creating a new function identity on every `useMemo` recomputation.
 */
export const EllipsisCell = ({ value }: { value: string }) => (
  <div title={value ?? ""} style={ellipsisStyle}>
    {value ?? ""}
  </div>
);

/**
 * React hook that returns a **new** array of `GridDataTableColumn`
 * definitions where every column with `ellipsisCell: true` has a `Cell`
 * renderer that truncates overflowing text with an ellipsis (`…`).
 *
 * The original column objects are **not** mutated — the hook produces
 * shallow copies with the `Cell` property set.
 *
 * The result is memoized via `useMemo` and only recomputes when the
 * `columns` reference changes.
 *
 * @example
 * ```tsx
 * const columns: GridDataTableColumn[] = [
 *   {
 *     Header: 'Name',
 *     accessor: 'name',
 *     width: 300,
 *     ellipsisCell: true,
 *   },
 * ];
 *
 * const mappedColumns = useGridTextEllipsisCell({ columns });
 *
 * const tableState = useDataTable({
 *   initialColumns: mappedColumns,
 *   // ...
 * });
 * ```
 */
export function useGridTextEllipsisCell({
  columns,
}: UseGridTextEllipsisCellOptions): GridDataTableColumn[] {
  return useMemo(() => {
    return columns.map((column) => {
      if (!column.ellipsisCell) {
        return column;
      }

      return {
        ...column,
        Cell: EllipsisCell,
      };
    });
  }, [columns]);
}
